import { ErrorHandler, Injectable } from '@angular/core';

const CHUNK_RELOAD_KEY = 'chunk_reload_attempted';

/**
 * Detects whether an error is a chunk/module load failure.
 *
 * These occur when a user has the app open during a deployment and
 * lazy-loaded routes reference chunk files that no longer exist on the server.
 */
function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message ?? '';
    return (
      message.includes('Loading chunk') ||
      message.includes('ChunkLoadError') ||
      message.includes('Failed to fetch dynamically imported module')
    );
  }
  // Some bundlers wrap the error in a rejection reason string
  if (typeof error === 'string') {
    return (
      error.includes('Loading chunk') ||
      error.includes('ChunkLoadError') ||
      error.includes('Failed to fetch dynamically imported module')
    );
  }
  return false;
}

/**
 * Global error handler that intercepts chunk load failures and forces a page
 * reload so the browser fetches the latest assets after a deployment.
 *
 * Uses sessionStorage to prevent infinite reload loops: the flag is set before
 * reloading and cleared on successful app bootstrap (via `clearChunkReloadFlag`).
 */
@Injectable()
export class ChunkErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    if (isChunkLoadError(error) && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, Date.now().toString());
      window.location.reload();
      return;
    }

    // Default behaviour: log to console
    console.error(error);
  }
}

/**
 * Clears the chunk-reload guard flag.
 * Call this once during app initialisation to allow future reloads
 * if another deployment happens while the session is still active.
 */
export function clearChunkReloadFlag(): void {
  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
}
